/**
 * 4D Modelling Page — Phases 10-16
 * Resizable split: 3D Viewer (top) + Schedule/Object Panel (bottom) + Timeliner
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { authFetch } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { usePermissions } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { toast } from 'sonner';
import {
  Upload, Link2, Wand2, RefreshCw, Maximize2, Eye,
  Box, Activity, AlertTriangle, CheckCircle2,
  Monitor, ChevronsUp, ChevronsDown, Trash2, Gauge, Sparkles, CalendarRange, Film
} from 'lucide-react';

import FourDViewer from '@/components/fourd/FourDViewer';
import { FourDTimeliner } from '@/components/fourd/FourDTimeliner';
import { FloatingLegend } from '@/components/fourd/FloatingLegend';
import { ActivitiesGrid } from '@/components/fourd/ActivitiesGrid';
import { ObjectTreePanel } from '@/components/fourd/ObjectTreePanel';
import { ProgressOverlay } from '@/components/fourd/ProgressOverlay';
import { DisciplineOverlayPanel } from '@/components/fourd/DisciplineOverlayPanel';
import { buildObjectTree, findAncestors } from '@/components/fourd/ObjectTree';
import { PresentationMode } from '@/components/fourd/PresentationMode';
import { FourDLookAhead } from '@/components/fourd/FourDLookAhead';
import { TimelinerVideoExport } from '@/components/fourd/TimelinerVideoExport';
import { MicroActivityDetail } from '@/components/fourd/MicroActivityDetail';
import { HEALTH_COLORS, macroHealth } from '@/components/fourd/microLinkRows';
import { LinkingPanel } from '@/components/fourd/LinkingPanel';
import { FourDLinkageHitl } from '@/components/fourd/FourDLinkageHitl';

import { useProject } from '@/contexts/ProjectContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FourDModelling() {
  const { projectId: PROJECT_ID } = useProject();
  const { can: canPerm } = usePermissions();
  const can4DCreate = canPerm('4D_MODEL', 'create');
  const can4DEdit = canPerm('4D_MODEL', 'edit');
  // Models & data
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [scheduleProjects, setScheduleProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [kpiColorSets, setKpiColorSets] = useState([]);
  const [selectedKpiSet, setSelectedKpiSet] = useState(null);

  // Timeliner
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [currentSnapshot, setCurrentSnapshot] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(0.5);
  const playRef = useRef(null);

  // Viz
  const [vizMode, setVizMode] = useState('PLAN_VS_ACTUAL');
  const [showLegend, setShowLegend] = useState(true);
  const [showKpiOverlay, setShowKpiOverlay] = useState(true);

  // UI
  const [selectedActivityDbIds, setSelectedActivityDbIds] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bottomTab, setBottomTab] = useState('activities');
  const [hitlOpen, setHitlOpen] = useState(false);
  const [lookaheadOpen, setLookaheadOpen] = useState(false);
  const [videoExport, setVideoExport] = useState({ open: false, basis: 'PLAN_VS_ACTUAL', fromDate: '', toDate: '' });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gridSearchText, setGridSearchText] = useState('');
  const [importingMapping, setImportingMapping] = useState(false);
  const [showPresentation, setShowPresentation] = useState(false);
  const [timelinerCollapsed, setTimelinerCollapsed] = useState(false);
  const [onlyLateCp, setOnlyLateCp] = useState(false);

  // Discipline schedules linked to each macro activity (engineering,
  // procurement, fabrication & construction, pre-comm & commissioning).
  const [microLinks, setMicroLinks] = useState([]);
  const [microLinkCounts, setMicroLinkCounts] = useState({});
  const [microRollup, setMicroRollup] = useState({});
  const [loadingMicroLinks, setLoadingMicroLinks] = useState(false);
  const [focusTick, setFocusTick] = useState(0);

  // Object tree
  const [flatObjectTree, setFlatObjectTree] = useState([]);
  const [objectTreeTotal, setObjectTreeTotal] = useState(0);
  const [objectTreeSearch, setObjectTreeSearch] = useState('');
  const [loadingTree, setLoadingTree] = useState(false);
  const [expandedTreeNodes, setExpandedTreeNodes] = useState(new Set());
  const [selectedTreeDbId, setSelectedTreeDbId] = useState(null);
  const [highlightedTreeDbId, setHighlightedTreeDbId] = useState(null);

  // Details
  const [selectedObjectDetail, setSelectedObjectDetail] = useState(null);
  const [selectedActivityDetail, setSelectedActivityDetail] = useState(null);

  const viewerRef = useRef(null);
  const selfSelectRef = useRef(0);
  const [microDetail, setMicroDetail] = useState(null);

  // ── Data Fetching ──
  const fetchModels = useCallback(async () => {
    if (!PROJECT_ID) return;
    try {
      const resp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models`);
      const data = await resp.json();
      const list = data.models || [];
      setModels(list);
      if (list.length > 0 && !selectedModelId) {
        const preferred = list.find(m => m.translationStatus === 'COMPLETE') || list[0];
        setSelectedModelId(preferred.modelId);
      }
    } catch (e) { console.error(e); }
  }, [selectedModelId, PROJECT_ID]);

  const fetchModelDetail = useCallback(async () => {
    if (!selectedModelId || !PROJECT_ID) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}`); setSelectedModel(await r.json()); } catch {}
  }, [selectedModelId]);

  const fetchMappings = useCallback(async () => {
    if (!selectedModelId) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/mappings?limit=2000`); const d = await r.json(); setMappings(d.mappings || []); } catch {}
  }, [selectedModelId]);

  const fetchSnapshots = useCallback(async () => {
    if (!selectedModelId) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/timeliner/snapshots?mode=${vizMode}`); const d = await r.json(); setSnapshots(d.snapshots || []); setSnapshotIndex(0); } catch {}
  }, [selectedModelId, vizMode]);

  const fetchSnapshotDetail = useCallback(async (index) => {
    if (!selectedModelId) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/timeliner/snapshot/${index}?mode=${vizMode}`); setCurrentSnapshot(await r.json()); } catch {}
  }, [selectedModelId, vizMode]);

  const fetchMicroLinks = useCallback(async () => {
    if (!selectedModelId) { setMicroLinks([]); setMicroLinkCounts({}); setMicroRollup({}); return; }
    setLoadingMicroLinks(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/micro-links`);
      if (!r.ok) throw new Error(`micro-links ${r.status}`);
      const d = await r.json();
      setMicroLinks(d.rows || []);
      setMicroLinkCounts(d.byDiscipline || {});
      setMicroRollup(d.rollup || {});
    } catch (e) {
      setMicroLinks([]); setMicroLinkCounts({}); setMicroRollup({});
      toast.error('Could not load the linked discipline activities.');
    }
    setLoadingMicroLinks(false);
  }, [selectedModelId, PROJECT_ID]);

  const fetchKpiColors = useCallback(async () => {
    if (!PROJECT_ID) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/kpi/color-sets`); const d = await r.json(); setKpiColorSets(d.colorSets || []); if (d.colorSets?.length > 0) setSelectedKpiSet(d.colorSets[0]); } catch {}
  }, [PROJECT_ID]);

  const fetchScheduleProjects = useCallback(async () => {
    if (!PROJECT_ID) return;
    try { const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/schedule-projects`); if (r.ok) { const d = await r.json(); setScheduleProjects(d.projects || []); } } catch {}
  }, [PROJECT_ID]);

  const fetchObjectTree = useCallback(async (search = '') => {
    if (!selectedModelId) return;
    setLoadingTree(true);
    try {
      const qs = search ? `&search=${encodeURIComponent(search)}` : '';
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/object-tree?limit=10000${qs}`);
      const d = await r.json();
      setFlatObjectTree(d.objects || []);
      setObjectTreeTotal(d.total || 0);
      if (!search) {
        const toExpand = new Set();
        for (const obj of (d.objects || [])) {
          if (obj.depth <= 2 && obj.childCount > 0) toExpand.add(obj.dbId);
        }
        setExpandedTreeNodes(toExpand);
      }
    } catch {}
    setLoadingTree(false);
  }, [selectedModelId]);

  const objectTreeRoots = useMemo(() => buildObjectTree(flatObjectTree), [flatObjectTree]);

  // ── Bidirectional Navigation ──
  const handleViewerObjectSelect = useCallback(async (dbId) => {
    // Our own painting selects objects in the viewer; that first event must not
    // pull the user off the Activities tab. Consumed once so genuine picks work.
    if (selfSelectRef.current && Date.now() - selfSelectRef.current < 5000) {
      selfSelectRef.current = 0;
      return;
    }
    selfSelectRef.current = 0;
    setSelectedActivityDetail(null);
    setHighlightedTreeDbId(dbId);
    const ancestors = findAncestors(flatObjectTree, dbId);
    setExpandedTreeNodes(prev => {
      const next = new Set(prev);
      for (const aid of ancestors) next.add(aid);
      return next;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-dbid="${dbId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    if (bottomTab !== 'objects') setBottomTab('objects');
    if (!selectedModelId) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/object-tree/${dbId}`);
      if (r.ok) { setSelectedObjectDetail(await r.json()); setSelectedTreeDbId(dbId); }
    } catch {}
  }, [flatObjectTree, selectedModelId, bottomTab]);

  const handleTreeNodeSelect = useCallback(async (dbId) => {
    setSelectedTreeDbId(dbId);
    setSelectedActivityDetail(null);
    setHighlightedTreeDbId(null);
    viewerRef.current?.selectAndZoom(dbId);
    if (!selectedModelId) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/object-tree/${dbId}`);
      if (r.ok) setSelectedObjectDetail(await r.json());
      else setSelectedObjectDetail({ dbId, name: `Object ${dbId}`, properties: {} });
    } catch { setSelectedObjectDetail({ dbId, name: `Object ${dbId}`, properties: {} }); }
  }, [selectedModelId]);

  const handleTreeToggle = useCallback((dbId) => {
    setExpandedTreeNodes(prev => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId); else next.add(dbId);
      return next;
    });
  }, []);

  const handleActivitySelect = useCallback((activityId) => {
    setSelectedObjectDetail(null);
    const m = mappings.find(x => x.activityId === activityId);
    setSelectedActivityDetail(m || null);
    if (!m) return;
    // A WBS row owns every object under it; a task owns its own.
    const prefix = `${m.activityCode || ''}.`;
    const family = m.isSummary
      ? mappings.filter(x => x.activityCode === m.activityCode
                          || String(x.activityCode || '').startsWith(prefix))
      : [m];
    const colour = HEALTH_COLORS[macroHealth(m)] || '#3B82F6';
    const hotspots = [];
    for (const row of family) {
      for (const o of (row.modelObjects || [])) {
        if (o?.dbId != null) {
          hotspots.push({ dbId: o.dbId, color: HEALTH_COLORS[macroHealth(row)] || colour });
        }
      }
    }
    // Paint + frame the linked objects and ghost the rest. Done here rather than
    // through selectedDbIds so the viewer's own selection event can't drag the
    // user off the Activities tab.
    if (hotspots.length > 0) {
      selfSelectRef.current = Date.now();
      const painted = viewerRef.current?.applyHotspot?.(hotspots);
      if (!painted) setSelectedActivityDbIds(hotspots.map(h => h.dbId));
    } else {
      toast.message('No 3D objects are linked to this activity yet.');
    }
  }, [mappings]);

  const handleHeatmapChange = useCallback((on) => {
    if (!on) { viewerRef.current?.clearHotspot?.(); return; }
    const hotspots = [];
    for (const m of mappings) {
      if (m.isSummary) continue;
      const colour = HEALTH_COLORS[macroHealth(m)];
      if (!colour) continue;
      for (const o of (m.modelObjects || [])) {
        if (o?.dbId != null) hotspots.push({ dbId: o.dbId, color: colour });
      }
    }
    if (hotspots.length === 0) { toast.message('No mapped 3D objects to paint yet.'); return; }
    selfSelectRef.current = Date.now();
    viewerRef.current?.applyHotspot?.(hotspots);
  }, [mappings]);

  // Clicking a Status KPI chip paints those activities' objects in their status
  // colour, isolates them and frames the camera on them.
  const handleStatusFilterPaint = useCallback((statuses) => {
    if (!statuses || statuses.length === 0) {
      viewerRef.current?.clearHotspot?.();
      return;
    }
    const wanted = new Set(statuses);
    const matched = mappings.filter(m => wanted.has(m.scheduleStatus || 'NOT_STARTED'));
    const seen = new Set();
    const hotspots = [];
    const push = (row, colour) => {
      for (const o of (row.modelObjects || [])) {
        if (o?.dbId == null || seen.has(o.dbId)) continue;
        seen.add(o.dbId);
        hotspots.push({ dbId: o.dbId, color: colour });
      }
    };
    for (const m of matched) {
      const colour = HEALTH_COLORS[m.scheduleStatus] || '#94A3B8';
      push(m, colour);
      // A WBS row carries no geometry of its own — paint everything beneath it.
      if (m.isSummary || m.activityType === 'SUMMARY') {
        const prefix = `${m.activityCode || ''}.`;
        if (m.activityCode) {
          for (const child of mappings) {
            if (child === m) continue;
            if (String(child.activityCode || '').startsWith(prefix)) push(child, colour);
          }
        }
      }
    }
    const activities = matched.length;
    if (hotspots.length === 0) {
      viewerRef.current?.clearHotspot?.();
      toast.message(activities > 0
        ? `${activities} activities in that status, but none have 3D objects linked yet.`
        : 'Nothing in that status.');
      return;
    }
    selfSelectRef.current = Date.now();
    const painted = viewerRef.current?.applyHotspot?.(hotspots);
    if (painted) {
      toast.success(`${hotspots.length} objects across ${activities} activities isolated in 3D`);
    } else {
      setSelectedActivityDbIds(hotspots.map(h => h.dbId));
    }
  }, [mappings]);

  // ── Effects ──
  // Reset state and re-fetch when project changes
  useEffect(() => {
    if (!PROJECT_ID) return;
    setModels([]); setSelectedModelId(null); setSelectedModel(null); setMappings([]);
    setSnapshots([]); setSnapshotIndex(0); setCurrentSnapshot(null); setFlatObjectTree([]);
    fetchModels(); fetchKpiColors(); fetchScheduleProjects();
  }, [PROJECT_ID]);
  useEffect(() => { if (selectedModelId) { fetchModelDetail(); fetchMappings(); fetchSnapshots(); fetchMicroLinks(); } }, [selectedModelId, vizMode]);
  // Auto-refresh model detail when the viewer succeeds at auto-recover /
  // probe — clears the orphan overlay without needing a full page reload.
  useEffect(() => {
    const h = () => fetchModelDetail();
    window.addEventListener('fourd-model-refresh', h);
    return () => window.removeEventListener('fourd-model-refresh', h);
  }, [fetchModelDetail]);
  useEffect(() => {
    if (!selectedModelId || !selectedModel || selectedModel.translationStatus !== 'TRANSLATING') return;
    const iv = setInterval(async () => {
      try {
        const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/status`);
        const d = await r.json();
        const st = d.translationStatus;
        if (st === 'COMPLETE') { toast.success('Translation complete!'); fetchModelDetail(); clearInterval(iv); }
        else if (st === 'FAILED') { toast.error(d.translationError || 'Translation failed.'); fetchModelDetail(); clearInterval(iv); }
        else if (st === 'BUCKET_ORPHANED') { toast.error('The 3D model service storage for this model is no longer reachable — recovery options are now shown in the viewer.'); fetchModelDetail(); clearInterval(iv); }
      } catch {}
    }, 8000);
    return () => clearInterval(iv);
  }, [selectedModelId, selectedModel?.translationStatus]);
  useEffect(() => { if (snapshots.length > 0 && snapshotIndex < snapshots.length) fetchSnapshotDetail(snapshotIndex); }, [snapshotIndex, snapshots]);

  // ── Critical Path helpers ─────────────────────────────────────
  // Filter objectStates for CRITICAL_PATH mode when "Only late CP" is on:
  // keep VISIBLE objects at opacity >= 0.75 (live / overdue / current window)
  // and hide the α=0.35 "upcoming" tail so PMs focus on slippage only.
  const displaySnapshot = useMemo(() => {
    if (!currentSnapshot) return currentSnapshot;
    if (vizMode !== 'CRITICAL_PATH' || !onlyLateCp) return currentSnapshot;
    const filtered = (currentSnapshot.objectStates || []).map(o => (
      o.state === 'VISIBLE' && (o.opacity || 0) < 0.7
        ? { ...o, state: 'GHOST', opacity: 0.05, colorHex: '#E0E0E0' }
        : o
    ));
    const visibleCount = filtered.filter(o => o.state === 'VISIBLE').length;
    return { ...currentSnapshot, objectStates: filtered,
             metrics: { ...(currentSnapshot.metrics || {}),
                        objectsVisible: visibleCount } };
  }, [currentSnapshot, vizMode, onlyLateCp]);

  // "Today + Zoom" — pan the viewer to whichever CP objects are currently
  // painted at full/high opacity (live, in-progress, overdue, or current
  // window).  Called after the timeliner jumps to the Today snapshot.
  const handleTodayZoom = useCallback(() => {
    // Jumping to Today also narrows the Activities table to what is live now.
    setBottomTab('activities');
    setFocusTick(t => t + 1);
    const snap = displaySnapshot || currentSnapshot;
    if (!snap) return;
    const live = (snap.objectStates || [])
      .filter(o => o.state === 'VISIBLE' && (o.opacity || 0) >= 0.7 && o.dbId != null)
      .map(o => o.dbId);
    if (live.length > 0) {
      viewerRef.current?.zoomToDbIds(live);
      return;
    }
    // Fall back to the nearest upcoming CP window so the user still sees
    // where the critical thread is heading rather than a blank pan.
    const upcoming = (snap.objectStates || [])
      .filter(o => o.state === 'VISIBLE' && o.dbId != null)
      .sort((a, b) => (b.opacity || 0) - (a.opacity || 0))
      .slice(0, 12)
      .map(o => o.dbId);
    if (upcoming.length > 0) viewerRef.current?.zoomToDbIds(upcoming);
  }, [displaySnapshot, currentSnapshot]);
  useEffect(() => { if ((bottomTab === 'objects' || bottomTab === 'linking') && selectedModelId && flatObjectTree.length === 0) fetchObjectTree(''); }, [bottomTab, selectedModelId]);
  useEffect(() => {
    if (isPlaying) {
      playRef.current = setInterval(() => {
        setSnapshotIndex(prev => { if (prev >= snapshots.length - 1) { setIsPlaying(false); return prev; } return prev + 1; });
      }, 1000 / playSpeed);
    } else { clearInterval(playRef.current); }
    return () => clearInterval(playRef.current);
  }, [isPlaying, playSpeed, snapshots.length]);

  // ── Toolbar Actions ──
  const handleUpload = (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    const file = fd.get('file');
    const modelName = fd.get('model_name') || '3D Model';
    const modelDescription = fd.get('model_description') || '';
    if (!file || (file instanceof File && file.size === 0)) {
      toast.error('Please choose a 3D model file first.');
      return;
    }

    setUploading(true); setUploadProgress(0);

    // 3-step direct-to-APS flow. The file bytes NEVER touch our ingress —
    // the browser PUTs directly to the 3D model service-signed S3 URL, so Cloudflare
    // body-size limits (which used to surface as HTML "502 Bad Gateway"
    // pages) no longer apply.
    (async () => {
      let init;
      try {
        const initResp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/upload/init`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name }),
        });
        const initText = await initResp.text();
        try { init = JSON.parse(initText); } catch { init = null; }
        if (!initResp.ok || !init?.uploadUrl) {
          toast.error(init?.detail || `Init failed (HTTP ${initResp.status}) — ${initText.slice(0, 200)}`);
          setUploading(false); return;
        }
      } catch (err) {
        toast.error(`Init request failed: ${err.message}`);
        setUploading(false); return;
      }

      // Step 2: PUT file directly to APS S3 with progress
      const putXhr = new XMLHttpRequest();
      putXhr.open('PUT', init.uploadUrl);
      putXhr.setRequestHeader('Content-Type', 'application/octet-stream');
      putXhr.timeout = 30 * 60 * 1000; // 30 min ceiling for very large models
      putXhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      };
      putXhr.onerror = () => { toast.error('Direct upload to the 3D model service failed. Check network / firewall.'); setUploading(false); setUploadProgress(0); };
      putXhr.ontimeout = () => { toast.error('Direct upload timed out after 30 min.'); setUploading(false); setUploadProgress(0); };
      putXhr.onload = async () => {
        if (putXhr.status < 200 || putXhr.status >= 300) {
          toast.error(`Upload storage rejected the file (HTTP ${putXhr.status}). ${putXhr.responseText.slice(0, 200)}`);
          setUploading(false); setUploadProgress(0);
          return;
        }
        // Step 3: Complete
        try {
          const compResp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/upload/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              bucketKey: init.bucketKey, objectKey: init.objectKey,
              uploadKey: init.uploadKey, urn: init.urn,
              model_name: modelName, model_description: modelDescription,
            }),
          });
          const compText = await compResp.text();
          let comp = null; try { comp = JSON.parse(compText); } catch { comp = null; }
          if (compResp.ok && comp?.modelId) {
            toast.success('Uploaded! SVF2 translation queued.');
            setSelectedModelId(comp.modelId);
            setShowUploadDialog(false);
            await fetchModels();
          } else {
            toast.error(comp?.detail || `Finalize failed (HTTP ${compResp.status}) — ${compText.slice(0, 200)}`);
          }
        } catch (err) {
          toast.error(`Finalize request failed: ${err.message}`);
        } finally {
          setUploading(false); setUploadProgress(0);
        }
      };
      putXhr.send(file);
    })();
  };

  const handleLinkSchedule = async (scheduleId, scheduleName) => {
    if (!selectedModelId) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/link-schedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scheduleId, scheduleName }),
      });
      const d = await r.json(); toast.success(`Linked: ${d.activitiesLinked} activities`);
      setShowLinkDialog(false); fetchModelDetail(); fetchMappings();
    } catch { toast.error('Link failed'); }
  };

  const handleAutoMap = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/mappings/auto`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ strategy: 'ALL' }),
      });
      const d = await r.json(); toast.success(`Mapped: ${d.matched} matched`); fetchMappings(); fetchModelDetail();
    } catch { toast.error('Auto-map failed'); }
    setSyncing(false);
  };

  const handleGenerateSnapshots = async () => {
    if (!selectedModelId) return; setGenerating(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/timeliner/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modes: ['PLAN', 'ACTUAL', 'PLAN_VS_ACTUAL', 'KPI', 'CRITICAL_PATH'] }),
      });
      const d = await r.json(); toast.success(`Generated ${d.totalSnapshots} snapshots`); fetchSnapshots();
    } catch { toast.error('Generation failed'); }
    setGenerating(false);
  };

  const handleSyncKpi = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/kpi/sync`, { method: 'POST' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || d.message || `KPI sync failed (${r.status})`); setSyncing(false); return;
      }
      toast.success('KPI synced'); fetchMappings();
    } catch { toast.error('Sync failed'); } setSyncing(false);
  };

  const handleSyncSchedule = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/sync-schedule`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || d.message || `Sync failed (${r.status})`); setSyncing(false); return; }
      const parts = [`${d.updated ?? 0} updated`];
      if (d.added) parts.push(`${d.added} added`);
      if (d.removed) parts.push(`${d.removed} removed`);
      const sc = d.statusCounts || {};
      const slipped = (sc.OVERDUE || 0) + (sc.NOT_STARTED_OVERDUE || 0) + (sc.DELAYED || 0);
      if (slipped) parts.push(`${slipped} slipped`);
      if (d.activitiesRestored) parts.push(`${d.activitiesRestored} 3D links restored`);
      toast.success(`Schedule synced: ${parts.join(', ')}`);
      // Sync also re-pulls the discipline schedules linked to each macro activity.
      fetchMappings(); fetchModelDetail(); fetchMicroLinks();
    } catch { toast.error('Sync failed'); } setSyncing(false);
  };

  const handleMappingExport = () => {
    if (!selectedModelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/mappings/export`;
    a.download = '4D_Mappings.xlsx'; a.click();
  };

  const handleMappingTemplate = () => {
    if (!selectedModelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/mappings/template`;
    a.download = '4D_Template.xlsx'; a.click();
  };

  const handleMappingImport = async (file) => {
    if (!selectedModelId || !file) return; setImportingMapping(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${selectedModelId}/mappings/import`, { method: 'POST', body: form });
      const d = await r.json();
      if (r.ok) { toast.success(`Imported: ${d.updated} updated`); fetchMappings(); fetchModelDetail(); }
      else toast.error(d.detail || 'Import failed');
    } catch (err) { toast.error(err.message); }
    setImportingMapping(false);
  };

  const handleDeleteModel = async () => {
    if (!selectedModelId || !selectedModel) return;
    const name = selectedModel.modelName || selectedModelId;
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will remove all associated data (object tree, mappings, timeliner snapshots). This action cannot be undone.`)) return;
    try {
      const source = selectedModel._source || '4D';
      const base = source === '5D' ? '5d' : '4d';
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/${base}/models/${selectedModelId}`, { method: 'DELETE' });
      if (!r.ok) { const d = await r.json(); toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`"${name}" deleted successfully`);
      setSelectedModelId(null); setSelectedModel(null); setMappings([]); setSnapshots([]);
      setCurrentSnapshot(null); setFlatObjectTree([]); setObjectTreeTotal(0);
      await fetchModels();
    } catch (err) { toast.error(err.message); }
  };

  const stats = selectedModel?.mappingStats || {};

  // ── Render ──
  return (
    <DashboardLayout>
      {PROJECT_ID && <AIAnalystPanel projectId={PROJECT_ID} moduleKey="FOURD_MODELLING" defaultOpen={false} compact={true} />}

      <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : 'h-[calc(100vh-4rem)]'}`} data-testid="fourd-page">
        <FourDToolbar
          models={models}
          selectedModelId={selectedModelId}
          setSelectedModelId={setSelectedModelId}
          selectedModel={selectedModel}
          vizMode={vizMode}
          setVizMode={setVizMode}
          kpiColorSets={kpiColorSets}
          selectedKpiSet={selectedKpiSet}
          setSelectedKpiSet={setSelectedKpiSet}
          showLegend={showLegend}
          setShowLegend={setShowLegend}
          showKpiOverlay={showKpiOverlay}
          setShowKpiOverlay={setShowKpiOverlay}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          syncing={syncing}
          generating={generating}
          onUpload={can4DCreate ? () => setShowUploadDialog(true) : undefined}
          onLinkSchedule={can4DEdit ? () => setShowLinkDialog(true) : undefined}
          onAutoMap={can4DEdit ? handleAutoMap : undefined}
          onSyncKpi={handleSyncKpi}
          onGenerateSnapshots={handleGenerateSnapshots}
          onPresentationMode={() => setShowPresentation(true)}
          onDeleteModel={handleDeleteModel}
          onOpenHitl={() => {
            if (!selectedModelId) { toast.error('Select a 3D model first'); return; }
            setLookaheadOpen(false);
            setHitlOpen(o => !o);
          }}
          hitlOpen={hitlOpen}
          onOpenLookahead={() => {
            if (!selectedModelId) { toast.error('Select a 3D model first'); return; }
            setHitlOpen(false);
            setLookaheadOpen(o => !o);
          }}
          lookaheadOpen={lookaheadOpen}
          onExportVideo={() => {
            if (!selectedModelId) { toast.error('Select a 3D model first'); return; }
            setVideoExport({ open: true, basis: vizMode, fromDate: '', toDate: '' });
          }}
        />

        <ResizablePanelGroup direction="vertical" className="flex-1" data-testid="resizable-layout">
          <ResizablePanel defaultSize={55} minSize={20} data-testid="viewer-panel">
            <ViewerSection
              viewerRef={viewerRef}
              selectedModel={selectedModel}
              currentSnapshot={displaySnapshot}
              onObjectSelect={handleViewerObjectSelect}
              selectedActivityDbIds={selectedActivityDbIds}
              vizMode={vizMode}
              selectedKpiSet={selectedKpiSet}
              showLegend={showLegend}
              setShowLegend={setShowLegend}
              showKpiOverlay={showKpiOverlay}
              stats={stats}
              snapshots={snapshots}
              snapshotIndex={snapshotIndex}
              setSnapshotIndex={setSnapshotIndex}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              playSpeed={playSpeed}
              setPlaySpeed={setPlaySpeed}
              projectId={PROJECT_ID}
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={45} minSize={15} data-testid="schedule-panel">
            {lookaheadOpen ? (
              <FourDLookAhead
                projectId={PROJECT_ID}
                modelId={selectedModelId}
                viewerRef={viewerRef}
                onClose={() => { setLookaheadOpen(false); viewerRef.current?.clearHotspot?.(); }}
                onExportClip={({ fromDate, toDate, basis }) =>
                  setVideoExport({ open: true, basis, fromDate, toDate })}
              />
            ) : hitlOpen ? (
              <FourDLinkageHitl
                projectId={PROJECT_ID}
                modelId={selectedModelId}
                scheduleId={selectedModel?.linkedScheduleId}
                viewerRef={viewerRef}
                canEdit={can4DEdit}
                onClose={() => { setHitlOpen(false); viewerRef.current?.clearHotspot?.(); }}
                onCommitted={() => { fetchMappings(); fetchModelDetail(); }}
              />
            ) : (
            <Tabs value={bottomTab} onValueChange={setBottomTab} className="flex flex-col h-full">
              <TabsList className="grid w-full grid-cols-4 h-8 rounded-none shrink-0">
                <TabsTrigger value="activities" className="text-xs h-7" data-testid="tab-activities">Activities</TabsTrigger>
                <TabsTrigger value="objects" className="text-xs h-7" data-testid="tab-objects">Object Tree & Properties</TabsTrigger>
                <TabsTrigger value="linking" className="text-xs h-7" data-testid="tab-linking">Linking</TabsTrigger>
                <TabsTrigger value="overlays" className="text-xs h-7" data-testid="tab-overlays">KPI Overlays</TabsTrigger>
              </TabsList>
              <TabsContent value="activities" className="flex-1 overflow-hidden m-0">
                <ActivitiesGrid
                  mappings={mappings}
                  microLinks={microLinks}
                  microLinkCounts={microLinkCounts}
                  rollup={microRollup}
                  loadingMicroLinks={loadingMicroLinks}
                  onMicroRowClick={(row) => setMicroDetail({ linkId: row.linkId, health: row._health })}
                  onHeatmapChange={handleHeatmapChange}
                  onStatusFilterChange={handleStatusFilterPaint}
                  timelineDate={(snapshots[snapshotIndex]?.snapshotDate || '').slice(0, 10) || null}
                  timelinePlaying={isPlaying}
                  focusTick={focusTick}
                  gridSearchText={gridSearchText}
                  onGridSearchChange={setGridSearchText}
                  onRowClicked={handleActivitySelect}
                  onSyncSchedule={handleSyncSchedule}
                  syncing={syncing}
                  onMappingTemplate={handleMappingTemplate}
                  onMappingExport={handleMappingExport}
                  onMappingImport={handleMappingImport}
                  importingMapping={importingMapping}
                  onCriticalFilterChange={(on) => {
                    if (!on) { viewerRef.current?.clearHotspot?.(); return; }
                    const cpDbIds = [];
                    (mappings || []).forEach(m => {
                      if (m.isCriticalPath && !m.isSummary) {
                        (m.modelObjects || []).forEach(mo => { if (mo?.dbId != null) cpDbIds.push(mo.dbId); });
                      }
                    });
                    if (cpDbIds.length > 0) {
                      viewerRef.current?.applyHotspot?.(cpDbIds.map(dbId => ({ dbId, color: '#DC2626' })));
                    } else {
                      toast?.info?.('No critical-path activities have linked 3D objects yet.');
                    }
                  }}
                />
              </TabsContent>
              <TabsContent value="objects" className="flex-1 overflow-hidden m-0">
                <ObjectTreePanel
                  modelId={selectedModelId}
                  projectId={PROJECT_ID}
                  objectTreeRoots={objectTreeRoots}
                  flatObjectTreeLength={flatObjectTree.length}
                  objectTreeTotal={objectTreeTotal}
                  loadingTree={loadingTree}
                  objectTreeSearch={objectTreeSearch}
                  onSearchChange={setObjectTreeSearch}
                  onSearch={() => fetchObjectTree(objectTreeSearch)}
                  expandedTreeNodes={expandedTreeNodes}
                  onToggle={handleTreeToggle}
                  selectedTreeDbId={selectedTreeDbId}
                  onSelect={handleTreeNodeSelect}
                  highlightedTreeDbId={highlightedTreeDbId}
                  selectedObjectDetail={selectedObjectDetail}
                  selectedActivityDetail={selectedActivityDetail}
                  onLocateInViewer={(dbId) => viewerRef.current?.selectAndZoom(dbId)}
                />
              </TabsContent>
              <TabsContent value="linking" className="flex-1 overflow-hidden m-0">
                <LinkingPanel
                  modelId={selectedModelId}
                  projectId={PROJECT_ID}
                  mappings={mappings}
                  objectTreeRoots={objectTreeRoots}
                  flatObjectTreeLength={flatObjectTree.length}
                  expandedTreeNodes={expandedTreeNodes}
                  onToggle={handleTreeToggle}
                  onRefreshMappings={fetchMappings}
                />
              </TabsContent>
              <TabsContent value="overlays" className="flex-1 overflow-hidden m-0">
                <DisciplineOverlayPanel
                  modelId={selectedModelId}
                  scheduleId={selectedModel?.linkedScheduleId}
                  projectId={PROJECT_ID}
                  viewerRef={viewerRef}
                  onOverlayActive={(on) => setShowLegend(!on)}
                />
              </TabsContent>
            </Tabs>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>

        <div className={`border-t shrink-0 transition-all duration-200 ${timelinerCollapsed ? 'h-8' : 'h-36'}`} data-testid="timeliner-section">
          <div className="flex items-center justify-between px-2 h-8 border-b bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground">4D Timeliner</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[10px] gap-0.5"
              onClick={() => setTimelinerCollapsed(!timelinerCollapsed)}
              data-testid="timeliner-collapse-toggle"
            >
              {timelinerCollapsed ? <ChevronsDown className="w-3 h-3" /> : <ChevronsUp className="w-3 h-3" />}
              {timelinerCollapsed ? 'Expand' : 'Collapse'}
            </Button>
          </div>
          {!timelinerCollapsed && (
            <FourDTimeliner
              snapshots={snapshots}
              currentIndex={snapshotIndex}
              onIndexChange={setSnapshotIndex}
              isPlaying={isPlaying}
              onTogglePlay={() => setIsPlaying(!isPlaying)}
              playSpeed={playSpeed}
              onSpeedChange={setPlaySpeed}
              vizMode={vizMode}
              onlyLateCp={onlyLateCp}
              onOnlyLateCpChange={setOnlyLateCp}
              onTodayZoom={handleTodayZoom}
            />
          )}
        </div>
      </div>

      <UploadDialog open={showUploadDialog} onOpenChange={setShowUploadDialog} onSubmit={handleUpload} uploading={uploading} progress={uploadProgress} />
      <MicroActivityDetail
        open={!!microDetail}
        onOpenChange={(o) => { if (!o) setMicroDetail(null); }}
        projectId={PROJECT_ID}
        modelId={selectedModelId}
        linkId={microDetail?.linkId}
        health={microDetail?.health}
      />
      <TimelinerVideoExport
        open={videoExport.open}
        onOpenChange={(o) => setVideoExport(v => ({ ...v, open: o }))}
        projectId={PROJECT_ID}
        modelId={selectedModelId}
        modelName={selectedModel?.modelName}
        mappings={mappings}
        viewerRef={viewerRef}
        initialBasis={videoExport.basis}
        initialFrom={videoExport.fromDate}
        initialTo={videoExport.toDate}
      />
      <LinkDialog open={showLinkDialog} onOpenChange={setShowLinkDialog} projects={scheduleProjects} onLink={handleLinkSchedule} />

      {showPresentation && (
        <PresentationMode
          onClose={() => setShowPresentation(false)}
          snapshots={snapshots}
          currentIndex={snapshotIndex}
          onIndexChange={setSnapshotIndex}
          isPlaying={isPlaying}
          onTogglePlay={() => setIsPlaying(!isPlaying)}
          playSpeed={playSpeed}
          onSpeedChange={setPlaySpeed}
          currentSnapshot={currentSnapshot}
          vizMode={vizMode}
        >
          <FourDViewer
            modelUrn={selectedModel?.apsDerivativeUrn || selectedModel?.apsModelUrn}
            translationStatus={selectedModel?.translationStatus}
            apsBucketOrphaned={selectedModel?.apsBucketOrphaned}
            hasSourceBackup={selectedModel?.hasSourceBackup}
            modelId={selectedModel?.modelId || selectedModel?._id}
            onRecovered={typeof window !== 'undefined' ? () => window.dispatchEvent(new CustomEvent('fourd-model-refresh')) : undefined}
            currentSnapshot={currentSnapshot}
            selectedDbIds={[]}
            viewerSettings={selectedModel?.viewerSettings}
            projectId={PROJECT_ID}
          />
          {/* KPI Overlay — as-of-today snapshot (top-center of viewer, mirrors 5D) */}
          {showKpiOverlay && stats && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-background/85 backdrop-blur-md border rounded-lg px-3 py-1 z-10 text-[10px]" data-testid="fourd-presentation-kpi-overlay">
              <span className="uppercase tracking-wider font-semibold" style={{ color: 'var(--text-muted)' }}>
                As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
              </span>
              <span>Progress <strong style={{ color: '#3B82F6' }}>{(currentSnapshot?.avgProgress ?? stats.avgProgress ?? 0).toFixed(1)}%</strong></span>
              <span>SPI <strong style={{ color: (currentSnapshot?.spi ?? 1) >= 1 ? '#22C55E' : (currentSnapshot?.spi ?? 1) >= 0.9 ? '#F59E0B' : '#EF4444' }}>{(currentSnapshot?.spi ?? 1).toFixed(2)}</strong></span>
              <span>Activities <strong>{stats.mappedActivities || 0}/{stats.totalActivities || 0}</strong></span>
              <span>Objects <strong>{stats.mappedModelObjects || 0}/{stats.totalModelObjects || 0}</strong></span>
              <span>Coverage <strong style={{ color: '#8B5CF6' }}>{(stats.mappingCoveragePct || 0).toFixed(0)}%</strong></span>
            </div>
          )}
          <ProgressOverlay
            snapshots={snapshots}
            currentIndex={snapshotIndex}
            onIndexChange={setSnapshotIndex}
            isPlaying={isPlaying}
            onTogglePlay={() => setIsPlaying(!isPlaying)}
            playSpeed={playSpeed}
            onSpeedChange={setPlaySpeed}
            currentSnapshot={currentSnapshot}
            vizMode={vizMode}
          />
        </PresentationMode>
      )}
    </DashboardLayout>
  );
}

// ── Toolbar ──
function FourDToolbar({
  models, selectedModelId, setSelectedModelId, selectedModel,
  vizMode, setVizMode, kpiColorSets, selectedKpiSet, setSelectedKpiSet,
  showLegend, setShowLegend, showKpiOverlay, setShowKpiOverlay,
  isFullscreen, setIsFullscreen,
  syncing, generating,
  onUpload, onLinkSchedule, onAutoMap, onSyncKpi, onGenerateSnapshots,
  onPresentationMode, onDeleteModel, onOpenHitl, hitlOpen,
  onOpenLookahead, lookaheadOpen, onExportVideo
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap" data-testid="fourd-toolbar">
      <div className="flex items-center gap-1.5">
        <Box className="w-4 h-4 text-pink-500" />
        <span className="text-sm font-semibold">4D Modelling</span>
      </div>
      <Select value={selectedModelId || ''} onValueChange={setSelectedModelId}>
        <SelectTrigger className="h-7 w-48 text-xs" data-testid="model-selector">
          <SelectValue placeholder="Select Model" />
        </SelectTrigger>
        <SelectContent>
          {models.map(m => (
            <SelectItem key={m.modelId} value={m.modelId}>{m.modelName}{m._source === '5D' ? ' (5D)' : ''}</SelectItem>
          ))}
          {models.length === 0 && <SelectItem value="none" disabled>No models</SelectItem>}
        </SelectContent>
      </Select>
      {selectedModelId && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onDeleteModel} data-testid="delete-model-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete Model</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {selectedModel?.linkedScheduleName && (
        <Badge variant="secondary" className="text-[10px] h-6">
          <Link2 className="w-3 h-3 mr-1" />{selectedModel.linkedScheduleName}
        </Badge>
      )}
      <TranslationBadge status={selectedModel?.translationStatus} urn={selectedModel?.apsModelUrn} deriv={selectedModel?.apsDerivativeUrn} />
      <Select value={vizMode} onValueChange={setVizMode}>
        <SelectTrigger className="h-7 w-40 text-xs" data-testid="viz-mode-selector">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="PLAN_VS_ACTUAL">Plan vs Actual</SelectItem>
          <SelectItem value="PLAN">Plan Only</SelectItem>
          <SelectItem value="FORECAST">Forecast Only</SelectItem>
          <SelectItem value="ACTUAL">Actual Only</SelectItem>
          <SelectItem value="KPI">KPI Overlay</SelectItem>
          <SelectItem value="CRITICAL_PATH">Critical Path</SelectItem>
        </SelectContent>
      </Select>
      {vizMode === 'KPI' && (
        <Select value={selectedKpiSet?.colorSetName || ''} onValueChange={v => setSelectedKpiSet(kpiColorSets.find(s => s.colorSetName === v))}>
          <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="KPI Palette" /></SelectTrigger>
          <SelectContent>{kpiColorSets.map(s => <SelectItem key={s.colorSetName} value={s.colorSetName}>{s.colorSetName}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <div className="ml-auto flex items-center gap-1">
        <TooltipProvider>
          <ToolbarBtn icon={Upload} tip="Upload 3D Model" onClick={onUpload} testId="upload-model-btn" />
          <ToolbarBtn icon={Link2} tip="Link Schedule" onClick={onLinkSchedule} testId="link-schedule-btn" />
          <ToolbarBtn icon={Wand2} tip="Auto-Map" onClick={onAutoMap} disabled={syncing} testId="auto-map-btn" />
          <ToolbarBtn icon={RefreshCw} tip="Sync KPI" onClick={onSyncKpi} disabled={syncing} spin={syncing} testId="sync-kpi-btn" />
          <ToolbarBtn icon={Activity} tip="Generate Timeliner" onClick={onGenerateSnapshots} disabled={generating} pulse={generating} testId="generate-snapshots-btn" />
          <ToolbarBtn icon={Eye} tip="Legend" onClick={() => setShowLegend(!showLegend)} testId="toggle-legend-btn" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant={showKpiOverlay ? 'default' : 'ghost'} size="sm" className="h-7 px-2 gap-1 text-[10px]" onClick={() => setShowKpiOverlay(!showKpiOverlay)} data-testid="toggle-kpi-overlay-btn">
                <Gauge className="w-3.5 h-3.5" /> KPI Overlay
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle KPI Overlay (as of today)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant={hitlOpen ? 'default' : 'ghost'} size="sm"
                      className={`h-7 px-2 gap-1 text-[10px] ${hitlOpen ? 'bg-orange-600 hover:bg-orange-700' : 'text-orange-600'}`}
                      onClick={onOpenHitl} data-testid="cognara-4d-btn">
                <Sparkles className="w-3.5 h-3.5" /> Cognara 4D
              </Button>
            </TooltipTrigger>
            <TooltipContent>Cognara linkage — AI proposes activity ↔ 3D links for your review</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant={lookaheadOpen ? 'default' : 'ghost'} size="sm"
                      className={`h-7 px-2 gap-1 text-[10px] ${lookaheadOpen ? 'bg-sky-600 hover:bg-sky-700' : 'text-sky-600'}`}
                      onClick={onOpenLookahead} data-testid="lookahead-4d-btn">
                <CalendarRange className="w-3.5 h-3.5" /> Look-Ahead
              </Button>
            </TooltipTrigger>
            <TooltipContent>Look-ahead report — paint the horizon on the model and animate it</TooltipContent>
          </Tooltip>
          <ToolbarBtn icon={Film} tip="Export timeliner video (MP4)" onClick={onExportVideo} testId="export-video-btn" />
          <ToolbarBtn icon={Monitor} tip="Presentation Mode" onClick={onPresentationMode} testId="presentation-mode-btn" />
          <ToolbarBtn icon={Maximize2} tip="Fullscreen" onClick={() => setIsFullscreen(!isFullscreen)} testId="fullscreen-btn" />
        </TooltipProvider>
      </div>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, tip, onClick, disabled, spin, pulse, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClick} disabled={disabled} data-testid={testId}>
          <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''} ${pulse ? 'animate-pulse' : ''}`} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

function TranslationBadge({ status, urn, deriv }) {
  if (status === 'TRANSLATING') return <Badge variant="outline" className="text-[10px] h-6 border-blue-500 text-blue-400" data-testid="translation-status"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Translating...</Badge>;
  if (status === 'COMPLETE' && deriv && urn !== 'demo-urn-no-real-model') return <Badge variant="outline" className="text-[10px] h-6 border-emerald-500 text-emerald-400" data-testid="translation-status"><CheckCircle2 className="w-3 h-3 mr-1" />3D Ready</Badge>;
  if (status === 'FAILED') return <Badge variant="outline" className="text-[10px] h-6 border-red-500 text-red-400" data-testid="translation-status"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
  if (status === 'DEMO') return <Badge variant="outline" className="text-[10px] h-6 border-slate-500 text-slate-400" data-testid="translation-status"><Box className="w-3 h-3 mr-1" />Demo</Badge>;
  return null;
}

// ── Viewer Section ──
function ViewerSection({ viewerRef, selectedModel, currentSnapshot, onObjectSelect, selectedActivityDbIds, vizMode, selectedKpiSet, showLegend, setShowLegend, showKpiOverlay, stats, snapshots, snapshotIndex, setSnapshotIndex, isPlaying, setIsPlaying, playSpeed, setPlaySpeed, projectId }) {
  return (
    <div className="relative h-full">
      <FourDViewer
        ref={viewerRef}
        modelUrn={selectedModel?.apsDerivativeUrn || selectedModel?.apsModelUrn}
        translationStatus={selectedModel?.translationStatus}
        apsBucketOrphaned={selectedModel?.apsBucketOrphaned}
        hasSourceBackup={selectedModel?.hasSourceBackup}
        modelId={selectedModel?.modelId || selectedModel?._id}
        onReplace={typeof window !== 'undefined' ? () => window.dispatchEvent(new CustomEvent('fourd-replace-model')) : undefined}
        onRecovered={typeof window !== 'undefined' ? () => window.dispatchEvent(new CustomEvent('fourd-model-refresh')) : undefined}
        currentSnapshot={currentSnapshot}
        onObjectSelect={onObjectSelect}
        selectedDbIds={selectedActivityDbIds}
        viewerSettings={selectedModel?.viewerSettings}
        projectId={projectId}
      />
      {/* KPI Overlay — as-of-today snapshot (top-center of viewer) */}
      {showKpiOverlay && stats && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-background/85 backdrop-blur-md border rounded-lg px-3 py-1 z-10 text-[10px]" data-testid="fourd-kpi-overlay">
          <span className="uppercase tracking-wider font-semibold" style={{ color: 'var(--text-muted)' }}>
            As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </span>
          <span>Progress <strong style={{ color: '#3B82F6' }}>{(currentSnapshot?.metrics?.actualProgressPct ?? stats.avgProgress ?? 0).toFixed(1)}%</strong></span>
          <span>Planned <strong style={{ color: '#8B5CF6' }}>{(currentSnapshot?.metrics?.plannedProgressPct ?? 0).toFixed(1)}%</strong></span>
          <span>SPI <strong style={{ color: (currentSnapshot?.metrics?.spi ?? 1) >= 1 ? '#22C55E' : (currentSnapshot?.metrics?.spi ?? 1) >= 0.9 ? '#F59E0B' : '#EF4444' }}>{(currentSnapshot?.metrics?.spi ?? 1).toFixed(2)}</strong></span>
          <span>Activities <strong>{stats.mappedActivities || 0}/{stats.totalActivities || 0}</strong></span>
          <span>Objects <strong>{stats.mappedModelObjects || 0}/{stats.totalModelObjects || 0}</strong></span>
          <span>Coverage <strong style={{ color: '#8B5CF6' }}>{(stats.mappingCoveragePct || 0).toFixed(0)}%</strong></span>
        </div>
      )}
      <ProgressOverlay
        snapshots={snapshots}
        currentIndex={snapshotIndex}
        onIndexChange={setSnapshotIndex}
        isPlaying={isPlaying}
        onTogglePlay={() => setIsPlaying(!isPlaying)}
        playSpeed={playSpeed}
        onSpeedChange={setPlaySpeed}
        currentSnapshot={currentSnapshot}
        vizMode={vizMode}
      />
      <FloatingLegend mode={vizMode} kpiColorSet={selectedKpiSet} visible={showLegend} onToggle={() => setShowLegend(false)} />
      {selectedModel && (
        <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-md border rounded-lg p-2 text-[10px] space-y-0.5 z-10" data-testid="mapping-stats-overlay">
          <div className="font-semibold text-xs mb-1">Mapping Coverage</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${stats.mappingCoveragePct || 0}%` }} />
            </div>
            <span className="font-mono">{(stats.mappingCoveragePct || 0).toFixed(0)}%</span>
          </div>
          <div className="text-muted-foreground">
            Activities: {stats.mappedActivities || 0}/{stats.totalActivities || 0} | Objects: {stats.mappedModelObjects || 0}/{stats.totalModelObjects || 0}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dialogs ──
function UploadDialog({ open, onOpenChange, onSubmit, uploading, progress = 0 }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="upload-dialog">
        <DialogHeader><DialogTitle>Upload 3D Model</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs">Model Name</Label>
            <Input name="model_name" defaultValue="Plant 3D Model" className="mt-1" data-testid="model-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input name="model_description" placeholder="Optional" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">3D Model File</Label>
            <Input name="file" type="file" accept=".rvt,.nwd,.ifc,.dwg,.stp,.3dm,.zip" className="mt-1" required data-testid="model-file-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Max 500 MB · SVF2 translation runs asynchronously after upload.</p>
          </div>
          {uploading && (
            <div className="space-y-1" data-testid="upload-progress">
              <div className="flex items-center justify-between text-[10px] font-mono">
                <span>{progress < 100 ? 'Uploading to the 3D model service…' : 'Finalizing translation job…'}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>Cancel</Button>
            <Button type="submit" disabled={uploading} data-testid="upload-submit-btn">{uploading ? `Uploading… ${progress}%` : 'Upload & Translate'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ open, onOpenChange, projects, onLink }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="link-dialog">
        <DialogHeader><DialogTitle>Link Macro Schedule</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-auto">
          {projects.length === 0 && <p className="text-sm text-muted-foreground">No schedule projects found.</p>}
          {projects.map(sp => (
            <div key={sp.id} className="p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors" onClick={() => onLink(sp.id, sp.name)} data-testid={`schedule-link-${sp.id}`}>
              <div className="font-medium text-sm">{sp.name}</div>
              <div className="text-xs text-muted-foreground">{sp.taskCount} activities ({sp.totalTasks} total)</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
